/**
 * Wraps every authenticated route.
 *
 * On mount we check localStorage for an access token. If one exists we call
 * /api/me to bootstrap the user store from the token (so a packaged-app
 * refresh doesn't kick the user back to /login on every reload). While that
 * call is in flight we render a centred spinner - without this gate the
 * router would Navigate to /login before the bootstrap had a chance.
 *
 * If /api/me 401s the token is invalid; clear it and redirect to /login.
 */
import { useEffect, type ReactNode } from 'react';
import { Navigate } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useAuthStore } from '../shared/hooks/useAuthStore';
import { identityApi } from '../shared/api/identity';
import { ApiError, getAccessToken, setAccessToken } from '../shared/api/client';
import { useOnlineStatus } from '../shared/hooks/useOnlineStatus';

export function ProtectedRoute({ children }: { children: ReactNode }) {
  const user = useAuthStore((s) => s.user);
  const setUser = useAuthStore((s) => s.setUser);
  const queryClient = useQueryClient();
  const online = useOnlineStatus();

  const hasToken = !!getAccessToken();

  // Offline fallback: the persister hydrated React Query's cache from
  // IndexedDB before any component rendered. If a previously-online session
  // cached the `me` response, surface that user identity so the app boots
  // straight into the read-only experience instead of bouncing to /login
  // (which would itself fail without network).
  useEffect(() => {
    if (!online && !user && hasToken) {
      const cached = queryClient.getQueryData<{
        id: string;
        email: string;
        displayName: string;
        role: 'ADMIN' | 'MANAGER' | 'MEMBER' | 'OBSERVER';
      }>(['me']);
      if (cached) {
        setUser({
          id: cached.id,
          email: cached.email,
          displayName: cached.displayName,
          role: cached.role,
        });
      }
    }
  }, [online, user, hasToken, queryClient, setUser]);

  // Only fetch /me when we have a token but no user in memory - this is the
  // post-reload path. After login, user is already populated and the query
  // is `enabled: false`.
  const meQuery = useQuery({
    queryKey: ['me'],
    queryFn: () => identityApi.me(),
    enabled: !user && hasToken,
    retry: false,
    staleTime: 60_000,
  });

  useEffect(() => {
    if (meQuery.data && !user) {
      setUser({
        id: meQuery.data.id,
        email: meQuery.data.email,
        displayName: meQuery.data.displayName,
        role: meQuery.data.role,
      });
    }
  }, [meQuery.data, user, setUser]);

  // Token in storage but server says invalid - wipe + send to login.
  useEffect(() => {
    if (meQuery.error instanceof ApiError && meQuery.error.status === 401) {
      setAccessToken(null);
    }
  }, [meQuery.error]);

  if (user) return <>{children}</>;

  // No token at all → straight to login.
  if (!hasToken) return <Navigate to="/login" replace />;

  // Token exists but bootstrap is still in flight → centred spinner so the
  // router doesn't flash /login under the user.
  if (meQuery.isPending) {
    return (
      <div className="h-full w-full grid place-items-center text-slate-500">
        <div className="flex items-center gap-3">
          <span
            className="inline-block h-4 w-4 rounded-full border-2 border-slate-300 border-t-brand-500 animate-spin"
            aria-hidden="true"
          />
          <span className="text-sm">Restoring session…</span>
        </div>
      </div>
    );
  }

  // Bootstrap finished but no user (server 401, user disabled, network error
  // we couldn't recover from) → /login.
  return <Navigate to="/login" replace />;
}
